import React from 'react'
import i18n from 'i18next'
import { observable, makeObservable } from 'mobx'
import * as R from 'ramda'

import Product from './model/Product'
import Anchor from '../UniformComponents/Anchor'
import Tooltip from '../UniformComponents/Tooltip'

class ProductSearchStore {
    @observable filteredList = []

    @observable portfolios = [{
        value: '',
        label: 'View all',
    }]

    @observable searchProductTypes = []

    defaultPortfolio = { value: '', label: 'View all' }

    filter = {
        portfolio: this.defaultPortfolio,
        productType: {},
    }

    columns = []

    portfolioMap = new Map()

    constructor(store) {
        makeObservable(this)
        this.appStore = store
        this.product = new Product({}, store)
    }

    initSearch = async () => {
        try {
            await this.initStore()
            const products = await this.getProducts()
            const productsWithPortfolioName = this.addPortfolioNameTo(products)
            this.products = productsWithPortfolioName
            this.filteredList = this.products
        } catch (error) {
            console.log('ProductSearchStore initSearch error ', error)
        }
    }

    initStore = async () => {
        const [portfolios, industries] = await Promise.all([
            this.getPortfolios(),
            this.getIndustries(),
        ])

        const productTypes = await Promise.all(
            industries.map(this.getProductTypes),
        )

        this.allPortfolios = [this.defaultPortfolio].concat(
            portfolios.map(({ portfolioId, name, industry }) => ({
                value: portfolioId,
                label: name,
                industry,
            })).sort((a, b) => a.label.localeCompare(b.label)),
        )

        this.portfolios = this.allPortfolios

        this.industries = industries

        this.allProductTypes = industries.reduce((list, industry, index) => {
            const industryProductTypes = productTypes[index].map(({ code, name }) => ({
                value: code,
                label: name,
                selected: true,
                industry: industry.code,
                industryName: industry.name,
            }))
            return list.concat(industryProductTypes)
        }, [])

        this.searchProductTypes = R.sortBy(R.prop('label'))(this.allProductTypes.map(({
            value, label, selected, industryName,
        }) => ({
            value,
            label: `${industryName} - ${label}`,
            selected,
        })))

        this.searchProductTypes.forEach(({ value, selected }) => {
            this.filter.productType[value] = true
        })

        this.setPortfolioMap()
    }

    setPortfolioMap = () => {
        this.portfolios.forEach((p) => {
            if (!R.isEmpty(p.value)) this.portfolioMap.set(p.value, p.label)
        })
    }

    addPortfolioNameTo = (products) => {
        const updateProducts = products.map((p) => ({
            ...p,
            portfolioName: this.portfolioMap.get(p.portfolioId),
        }))
        return updateProducts
    }

    getProducts = async () => {
        const productResponse = await this.appStore.http.get('/product')

        return productResponse.map((product) => ({
            productId: product.productId,
            portfolioId: product.portfolioId,
            productType: product.productType,
            noSurveys: product.numberOfSurveys,
            name: `${product.name}${product.edition ? ` - ${product.edition}` : ''}`,
            status: product.status,
        }))
    }

    getPortfolios = async () => this.appStore.http.get('/portfolio')

    getProductTypes = ({ code }) => this.appStore.http.get(`/producttype/${code}`)

    getIndustries = () => this.appStore.http.get('/client/industry')

    changePortfolio = ({ label, value }) => {
        this.filter.portfolio = { label, value }
        this.search()
    }

    setProductTypeFilter = ({ value, name }) => {
        this.filter.productType[name] = value
        this.search()
    }

    resetFilter = () => {
        this.filter.portfolio = { value: '', label: 'View all' }
        this.searchProductTypes.forEach(({ value, selected }) => {
            this.filter.productType[value] = true
        })
        this.search()
    }

    search = () => {
        const { products } = this
        const { searchStr } = this.searchModule

        const portfolioFilter = ({ portfolioId }) => (
            this.filter.portfolio.value === ''
            || this.filter.portfolio.value === portfolioId
        )

        const productTypeFilter = ({ productType }) => (
            R.pathOr(false, [productType])(this.filter.productType)
        )

        const filteredProducts = products
            .filter(portfolioFilter)
            .filter(productTypeFilter)

        if (searchStr === '') {
            this.filteredList = filteredProducts
        } else if (!Number.isNaN(parseInt(searchStr, 10))) {
            this.filteredList = filteredProducts
                .filter((d) => d.productId === parseInt(searchStr, 10))
        } else {
            const re = new RegExp(searchStr, 'i')
            this.filteredList = filteredProducts
                .filter((d) => (
                    d.name.match(re)
                ))
        }
    }

    setColumns = (handleOpenProductModal) => {
        const { appStore } = this
        this.columns = [
            {
                name: i18n.t('event'),
                key: 'name',
                cellClass: 'bold',
                dataTransform: (name) => (
                    name.replace(/&amp;/gi, '&')
                ),
            },
            {
                name: i18n.t('num_surveys'),
                key: 'noSurveys',
                cellClass: 'center bold',
                headerClass: 'center',
            },
            {
                name: i18n.t('portfolio'),
                key: 'portfolioName',
                cellClass: 'bold',
            },
            {
                name: '',
                key: 'productId',
                cellClass: 'center',
                dataTransform: (productId, { status }) => (
                    <div className="survey-btn-wrapper">
                        {status !== 3
                        && (
                            <Anchor
                                cssClass="icon-link-btn"
                                onClick={(e) => e.stopPropagation()}
                                href={appStore.getDashboardLink('product', productId)}
                            >
                                <Tooltip
                                    text={i18n.t('product_dash')}
                                    direction="bottom"
                                />
                                <i className="fa-light fa-gauge-simple-max" />
                            </Anchor>
                        )}
                        <Anchor
                            cssClass="icon-link-btn"
                            onClick={(e) => e.stopPropagation()}
                            href={`/survey?event=${productId}`}
                        >
                            <Tooltip
                                text={i18n.t('product_surv')}
                                direction="bottom"
                            />
                            <i className="fa-light fa-square-poll-vertical" />
                        </Anchor>
                        {status !== 3
                        && (
                            <Anchor
                                cssClass="icon-link-btn"
                                onClick={() => {
                                    handleOpenProductModal(null, productId)
                                }}
                            >
                                <Tooltip
                                    text={i18n.t('product_edit')}
                                    direction="bottom"
                                />
                                <i className="fa-light fa-edit" />
                            </Anchor>
                        )}
                    </div>
                ),
            },
        ]
    }
}

export default ProductSearchStore
